import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import Button from '@/shared/components/flip-ui-kit/Button';
import Card from '@/shared/components/flip-ui-kit/Card';
import BorrowBannerCard from '@/shared/components/lending/BorrowBannerCard';
import { LendingPoolsTable } from '@/shared/components/lending/LendingPoolsTable';
import SupplyBannerCard from '@/shared/components/lending/SupplyBannerCard';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import BorrowIcon from '@/shared/icons/flip-ui-kit/large/Borrow';
import DollarIcon from '@/shared/icons/flip-ui-kit/large/Dollar';
import DropletIcon from '@/shared/icons/flip-ui-kit/large/Droplet';
import PercentageIcon from '@/shared/icons/flip-ui-kit/large/Percentage';
import { apyToText } from '@/shared/utils/boost';
import { DEFAULT_APY_PERIOD } from '@/shared/utils/lending';
import { formatWithNumeral } from '@/shared/utils/numbers';

export default function BorrowAndSupplyOverviewContent({
  onSupplyClick,
  onBorrowClick,
  getMarketRedirectUrl = (asset) => `/lending/markets/${asset}`,
}: {
  onSupplyClick: () => void;
  onBorrowClick: () => void;
  getMarketRedirectUrl?: (asset: ChainflipAsset) => string;
}) {
  const router = useRouter();
  const {
    isLoading: lendingPoolsLoading,
    lendingPools,
    totalSuppliedUsd,
    totalAvailableLiquidityUsd,
    totalBorrowedUsd,
  } = useLendingPools();

  const usdcBorrowRate = lendingPools.find(
    (pool) => pool.asset === 'Usdc',
  )?.defaultProjectedBorrowApy;

  const RowButton = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ asset }: { asset: ChainflipAsset }) => (
      <Button
        size="small"
        type="primary-standard"
        onClick={(e) => {
          e.stopPropagation();
          router.push(getMarketRedirectUrl(asset));
        }}
      >
        View Market
      </Button>
    ),
    [getMarketRedirectUrl, router],
  );

  return (
    <div className="flex flex-col gap-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SupplyBannerCard onClick={onSupplyClick} />
        <BorrowBannerCard onClick={onBorrowClick} />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={lendingPoolsLoading}
          icon={<DollarIcon />}
          title="Total Supplied"
          tooltip="The total value of funds supplied by lenders across all markets."
          footer="Current"
        >
          ${formatWithNumeral(totalSuppliedUsd, false)}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={lendingPoolsLoading}
          icon={<DropletIcon />}
          title="Total Available Liquidity"
          tooltip="The total value of funds available to borrow across all markets."
          footer="Current"
        >
          ${formatWithNumeral(totalAvailableLiquidityUsd, false)}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={lendingPoolsLoading}
          icon={<BorrowIcon />}
          title="Total Borrowed"
          tooltip="The total value of supplied assets that are lent out to borrowers."
          footer="Current"
        >
          ${formatWithNumeral(totalBorrowedUsd, false)}
        </Card>
        <Card
          className="w-full"
          skeletonLineWidth={100}
          loading={lendingPoolsLoading}
          icon={<PercentageIcon />}
          title="USDC Borrow Rate"
          tooltip={`The average annualized borrowing rate across the market over the past ${DEFAULT_APY_PERIOD.description}.`}
          footer={DEFAULT_APY_PERIOD.description}
        >
          {apyToText(usdcBorrowRate ?? 0)}
        </Card>
      </div>
      <LendingPoolsTable
        Button={RowButton}
        onRowClick={(chainflipAsset) => router.push(getMarketRedirectUrl(chainflipAsset))}
      />
    </div>
  );
}
